import { v4 } from "uuid";
import { supportsMediaSource } from "../../common/utilities/browser";
import { IDimensions } from "../types/item";
import { INetworkPhoto, IPhoto } from "../types/photo";
import { IGraphError, IGraphInnerError } from "./network";

/**
 * All photo API's go through one of the supported api domains. Depending on how
 * the service is configured these domains may vary.
 */
export interface IApiOriginMapping {
  authOrigin: string;
  badgerApiOrigin: string;
  feedbackOrigin: string;
  graphOrigin: string;
  odcOrigin: string;
  vroomOrigin: string;
}

/**
 * Set of API versions used to configure different network calls.
 * These are mapped to different prefixes dependening on the network origin.
 */
export interface IApiVersion {
  apiVersion1: string;
  apiVersion2: string;

  // Specific API versions.
  accountVersion: string;
  favoritesVersion: string;
}

/**
 * Features supported by specific domains.
 */
export interface IDomainConfiguration {
  badger?: boolean;
  scope: string;
  supportsAccessParam?: boolean;
}

/**
 * Settings used to configure how the network layer integrates with each of the
 * target domains.
 */
export interface IDomainSettings {
  [hostName: string]: IDomainConfiguration;
}

export interface IManifestURLOptions {
  baseUrl?: URL;
  format?: "dash" | "hls" | "mp4";
  pretranscode?: string;
  transcodeahead?: string;
  params?: URLSearchParams | { [parameterName: string]: string };
}

// Setup our mapping of api name to endpoint mapping.
export const apiOriginODC: IApiOriginMapping = {
  authOrigin: "",
  badgerApiOrigin: "https://api.badgerp.svc.ms",
  feedbackOrigin: "https://storage.live.com",
  graphOrigin: "https://graph.microsoft.com",
  odcOrigin: "https://storage.live.com",
  vroomOrigin: "https://api.onedrive.com"
};

export const apiOriginCOB: IApiOriginMapping = {
  authOrigin: "",
  badgerApiOrigin: "https://api.badgerp.svc.ms",
  feedbackOrigin: "https://storage.live.com",
  graphOrigin: "https://graph.microsoft.com",
  odcOrigin: "https://storage.live.com",
  vroomOrigin: "https://my.microsoftpersonalcontent.com"
};

export const apiVersionODC: IApiVersion = {
  apiVersion1: "v1.0",
  apiVersion2: "v1.0",

  // Specific API versions.
  accountVersion: "v2.1",
  favoritesVersion: "v2.1"
};

export const apiVersionCOB: IApiVersion = {
  apiVersion1: "_api/v2.0",
  apiVersion2: "_api/v2.1",

  // Specific API versions.
  accountVersion: "_api/v2.1",
  favoritesVersion: "_api/v2.1"
};

const domainSettings: IDomainSettings = {
  "api.onedrive.com": { badger: true, scope: "OneDrive.ReadWrite", supportsAccessParam: true },
  "graph.microsoft.com": { scope: "User.Read" },
  "my.microsoftpersonalcontent.com": { badger: true, scope: "OneDrive.ReadWrite" },
  "storage.live.com": { scope: "OneDrive.ReadWrite", supportsAccessParam: true }
};

// The current configration, we always start with the ODC configuration and this
// will be updated when we detect a COB migrated user.
const apiConfiguration: { apiOrigin: IApiOriginMapping; apiVersion: IApiVersion; domainSettings: IDomainSettings } = {
  apiOrigin: apiOriginODC,
  apiVersion: apiVersionODC,
  domainSettings
};

/**
 * Function to populate the required fields of url search params to fetch a manifest
 *
 * @param url baseUrl for the manifest
 * @param options
 */
export function addManifestParams(url: URL, options?: IManifestURLOptions): void {
  const params = getManifestParams(options ?? {});

  const entries = Array.from(params.entries());
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    url.searchParams.append(entry[0], entry[1]);
  }
}

const quoteExpression = /'/g;

/**
 * For OData strings that contain single quote (') these are escaped with double single quotes
 *
 * Example O'Fallon = O''Fallon
 *
 * @param value Raw input string with single quotes.
 * @returns The escapted string.
 */
export function escapeODataQuote(value: string): string {
  return value.replace(quoteExpression, "''");
}

/**
 * Function that returns required search parameters for the manifest file request
 *
 * @param options
 * @returns
 */
export function getManifestParams(options: IManifestURLOptions): URLSearchParams {
  const { params, format, pretranscode = "0", transcodeahead = "0" } = options;
  const _params = new URLSearchParams(params);

  _params.append("format", format ? format : supportsMediaSource() ? "dash" : "hls");
  _params.append("pretranscode", pretranscode);
  _params.append("transcodeahead", transcodeahead);
  _params.append("part", "index");
  _params.append("ccat", "2");
  _params.append("psi", v4());

  return _params;
}

/**
 * getApiConfiguration is used to get the set of properties used to control the
 * network. This includes domains, versions, and settings.
 */
export function getApiConfiguration(): { apiOrigin: IApiOriginMapping; apiVersion: IApiVersion; domainSettings: IDomainSettings } {
  return apiConfiguration;
}

/**
 * Used to convert a network photo into a application photo. This involves
 * ensuring we have required properties having the best values available.
 *
 * @param networkPhoto Incoming network photo (INetworkPhoto)
 * @returns The prepared application photo (IPhoto)
 */
export function preparePhoto(networkPhoto: INetworkPhoto): IPhoto {
  const { image, thumbnails, video } = networkPhoto;
  let dimensions: IDimensions;

  // Make sure we have a photo property, the code depends on it in the future.
  if (!networkPhoto.photo) {
    networkPhoto.photo = {};
  }

  // Ensure the photo has a dimensions property with the best value we can
  // determine from the photo / video.
  if (image) {
    dimensions = { height: image.height, width: image.width };
  } else if (video) {
    dimensions = { height: video.height, width: video.width };
  } else if (thumbnails) {
    dimensions = { height: thumbnails[0].large.height, width: thumbnails[0].large.width };
  } else {
    // We will default to 512x512 if no size if given.
    dimensions = { height: 512, width: 512 };
  }

  // Add the dimensions property to the photo.
  return Object.assign(networkPhoto, { dimensions });
}

/**
 * setApiConfiguration is used to update the origin and version mappings for
 * the network configuration.
 *
 * @param apiOrigin
 * @param apiVersion
 */
export function setApiConfiguration(apiOrigin: IApiOriginMapping, apiVersion: IApiVersion): void {
  apiConfiguration.apiOrigin = apiOrigin;
  apiConfiguration.apiVersion = apiVersion;
}

/**
 * Walks a given Graph error (inner error) and finds if any of the provided error codes are found, and if found,
 * which error code group index it was from as well as the matching error code, or [-1, undefined] if no match.
 *
 * @param graphError The error to inspect
 * @param errorCodeGroups A list of groups of error codes to match against
 * @returns A pair of the matching error code group index and the matched error code
 */
export function hasError(graphError: Partial<IGraphError> | undefined, errorCodeGroups: string[][]): [number, string | undefined] {
  let innerError: IGraphInnerError | undefined = graphError?.error;
  while (innerError) {
    for (let i = 0; i < errorCodeGroups.length; ++i) {
      if (errorCodeGroups[i].includes(innerError.code)) {
        return [i, innerError.code];
      }
    }

    innerError = innerError.innererror;
  }

  return [-1, undefined];
}
